import { Client } from "@xmpp/client"
import AdHocCommands from "../api/adHocCommands"
import { captureError } from "../ErrorHandlers"
import { AppThunk } from "./store"
import {
  dispatcherChatsFailed,
  dispatcherChatsRequested,
  setDispatcherChats,
} from "./dispatcherChatsSlice"

export const fetchDispatcherChats = (client: Client): AppThunk => {
  return async (dispatch) => {
    dispatch(dispatcherChatsRequested())
    try {
      dispatch(
        setDispatcherChats(await AdHocCommands.getDispatcherChats(client)),
      )
    } catch (error) {
      dispatch(dispatcherChatsFailed())
      captureError(error, { origin: "Other" })
    }
  }
}

/** Hand a chat to `dispatcherJid` (empty: to nobody), then reread the
 * mapping so the sections show where the chat ended up. Rejects when the
 * command does, so the caller can tell the manager. */
export const assignChatToDispatcher = (
  client: Client,
  { chatJid, dispatcherJid }: { chatJid: string; dispatcherJid: string },
): AppThunk<Promise<void>> => {
  return async (dispatch) => {
    await AdHocCommands.assignChatDispatcher({ client, chatJid, dispatcherJid })
    window.analytics.track("AssignChatDispatcher", { chatJid, dispatcherJid })
    await dispatch(fetchDispatcherChats(client))
  }
}

export type BulkAssignResult = {
  moved: string[]
  failed: { chatJid: string; error: Error }[]
}

/** Hand several chats to `dispatcherJid` (empty: to nobody), one after the
 * other, then reread the mapping once. One at a time on purpose: the backend
 * moves a chat without a lock, so parallel moves could race each other. A
 * failed chat does not stop the rest; the caller is told which ones failed. */
export const assignChatsToDispatcher = (
  client: Client,
  { chatJids, dispatcherJid }: { chatJids: string[]; dispatcherJid: string },
): AppThunk<Promise<BulkAssignResult>> => {
  return async (dispatch) => {
    const result: BulkAssignResult = { moved: [], failed: [] }
    for (const chatJid of chatJids) {
      try {
        await AdHocCommands.assignChatDispatcher({
          client,
          chatJid,
          dispatcherJid,
        })
        result.moved.push(chatJid)
      } catch (error) {
        result.failed.push({ chatJid, error: error as Error })
      }
    }
    window.analytics.track("AssignChatsDispatcher", {
      dispatcherJid,
      moved: result.moved.length,
      failed: result.failed.length,
    })
    await dispatch(fetchDispatcherChats(client))
    return result
  }
}
